import { ASSESSMENTFORMTXT, GENERAL_FORM, QuestionType } from '../../core/constants/common';
import FormEditService from '../../core/services/FromEditService';
import {
  buildOptionIdMap,
  cloneActionsForDuplicate,
  normalizeGridConfigForDuplicate,
  normalizeTableConfigForDuplicate,
  remapOptionId,
} from './payloadBuilders';

export const EXPORT_FORMAT = 'travanleo-form-export';
export const EXPORT_VERSION = '1.0';

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

// GET /forms/{formId} already returns the complete Section -> Subsection ->
// Question tree (fields, options, gridConfig, uom, etc. included), so the
// export tree is built straight from that response. This just stamps an
// explicit parentSectionId on every node so the hierarchy is unambiguous even
// outside the nested shape.
const buildSectionTree = (sections, parentSectionId = 0) => (
  (sections || []).map((section) => ({
    id: section.id,
    title: section.title,
    description: section.description,
    layout: section.layout,
    displayTitle: section.displayTitle,
    sortOrder: section.sortOrder,
    pageHelpText: section.pageHelpText,
    parentSectionId,
    scoringMethod: section.scoringMethod,
    weightage: section.weightage,
    uom: section.uom,
    multiSupportEnabled: section.multiSupportEnabled,
    fields: section.fields || [],
    subSections: buildSectionTree(section.subSections, section.id),
  }))
);

const collectFieldRefs = (sectionsTree, rootFields, refs = []) => {
  (rootFields || []).forEach((field) => refs.push({ field, sectionId: 0 }));
  const walk = (sections) => {
    (sections || []).forEach((section) => {
      (section.fields || []).forEach((field) => refs.push({ field, sectionId: section.id }));
      walk(section.subSections);
    });
  };
  walk(sectionsTree);
  return refs;
};

// Fetches the complete form (metadata, full Section -> Subsection -> Question
// hierarchy, options/gridConfig/UOM/validation, and per-question Smart
// Rules/Logic + Actions, which live behind separate endpoints from the main
// form/section payloads) and assembles it into a single importable JSON tree.
export const fetchFullFormExport = async (formId, langId) => {
  const formRes = await FormEditService.getFormDetails(formId, langId);
  const formData = formRes.data;

  const sectionsTree = buildSectionTree(formData.sections);
  const fieldRefs = collectFieldRefs(sectionsTree, formData.fields);
  const fieldFailures = [];

  await Promise.all(fieldRefs.map(async ({ field, sectionId }) => {
    try {
      const [actionsRes, logicsRes] = await Promise.all([
        FormEditService.getActions(formId, field.id),
        FormEditService.getFormFieldLogics(formId, field.id, sectionId),
      ]);
      field.actions = actionsRes.data || [];
      field.logicDefinitions = logicsRes.data?.logicDefinitions || [];
    } catch (err) {
      fieldFailures.push(field.name || field.id);
    }
  }));

  if (fieldFailures.length) {
    throw new Error(`Could not load Smart Rules/Actions for question(s): ${fieldFailures.join(', ')}. Please retry the export.`);
  }

  return {
    exportFormat: EXPORT_FORMAT,
    exportVersion: EXPORT_VERSION,
    exportedAt: new Date().toISOString(),
    form: {
      id: formData.id,
      name: formData.name,
      description: formData.description,
      formType: formData.formType,
      status: formData.status,
      version: formData.version,
      historicalDataSupported: formData.historicalDataSupported,
      selectedLanguage: formData.selectedLanguage,
    },
    fields: formData.fields || [],
    sections: sectionsTree,
  };
};

// ---------------------------------------------------------------------------
// Shared validation (used by both export, before download, and import, before
// any create calls are fired)
// ---------------------------------------------------------------------------

export const validateFormTree = (exportObject) => {
  const errors = [];
  if (!exportObject || typeof exportObject !== 'object') {
    return { valid: false, errors: ['File does not contain a valid form export.'] };
  }
  if (!exportObject.form?.id && exportObject.form?.id !== 0) {
    errors.push('Missing form metadata (id).');
  }
  if (!Array.isArray(exportObject.sections) && !Array.isArray(exportObject.fields)) {
    errors.push('Export has no sections or questions to work with.');
  }

  const seenSectionIds = new Set();
  const seenFieldIds = new Set();

  const checkField = (field, context) => {
    if (field.id == null) {
      errors.push(`A question in ${context} is missing an id.`);
    } else if (seenFieldIds.has(field.id)) {
      errors.push(`Duplicate question id ${field.id}.`);
    } else {
      seenFieldIds.add(field.id);
    }
    if (!field.name) errors.push(`Question ${field.id ?? '(unknown)'} is missing a name.`);
    if (!field.type || !Object.values(QuestionType).includes(field.type)) {
      errors.push(`Question ${field.id ?? field.name ?? '(unknown)'} has an unrecognized type "${field.type}".`);
    }
  };

  const checkSection = (section, context) => {
    if (section.id == null) {
      errors.push(`A section in ${context} is missing an id.`);
    } else if (seenSectionIds.has(section.id)) {
      errors.push(`Duplicate section id ${section.id}.`);
    } else {
      seenSectionIds.add(section.id);
    }
    if (!section.title) errors.push(`Section ${section.id ?? '(unknown)'} is missing a title.`);
    (section.fields || []).forEach((field) => checkField(field, `section "${section.title || section.id}"`));
    (section.subSections || []).forEach((sub) => checkSection(sub, `subsection of "${section.title || section.id}"`));
  };

  (exportObject.fields || []).forEach((field) => checkField(field, 'the form root'));
  (exportObject.sections || []).forEach((section) => checkSection(section, 'the form root'));

  return { valid: errors.length === 0, errors };
};

export const validateImportFile = (parsed) => {
  if (!parsed || typeof parsed !== 'object') {
    return { valid: false, errors: ['Selected file is not a valid form export.'] };
  }
  const errors = [];
  if (parsed.exportFormat !== EXPORT_FORMAT) {
    errors.push('This file was not produced by the Form Export feature.');
  }
  const structural = validateFormTree(parsed);
  errors.push(...structural.errors);
  return { valid: errors.length === 0, errors };
};

// ---------------------------------------------------------------------------
// Download helper
// ---------------------------------------------------------------------------

export const downloadFormExport = (exportObject, formName) => {
  const blob = new Blob([JSON.stringify(exportObject, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${formName || 'form'}-export.json`;
  a.click();
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// File reading
// ---------------------------------------------------------------------------

export const readImportFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    try {
      resolve(JSON.parse(reader.result));
    } catch (err) {
      reject(new Error('Selected file is not valid JSON.'));
    }
  };
  reader.onerror = () => reject(new Error('Failed to read the selected file.'));
  reader.readAsText(file);
});

// ---------------------------------------------------------------------------
// Import
// ---------------------------------------------------------------------------

const buildImportFieldPayload = (field, langId) => {
  const copy = JSON.parse(JSON.stringify(field));
  delete copy.id;
  delete copy.actions;
  delete copy.logicDefinitions;
  delete copy.logics;
  copy.selectedLanguage = { id: langId };
  copy.sortOrder = copy.sortOrder ?? 100;
  copy.active = copy.active ?? true;
  copy.options?.forEach((option) => { delete option.id; });
  if (copy.gridConfig) copy.gridConfig = normalizeGridConfigForDuplicate(copy.gridConfig);
  if (copy.tableConfig) copy.tableConfig = normalizeTableConfigForDuplicate(copy.tableConfig);
  return copy;
};

const remapLogicsForImport = (logics, optionIdMap, sectionIdMap, fieldIdMap) => (
  (logics || []).map((logic) => ({
    action: logic.action,
    rules: (logic.rules || []).map((rule) => ({
      operation: rule.operation,
      value: rule.value,
      option: { id: remapOptionId(rule.option?.id, optionIdMap) ?? 0 },
    })),
    displayFields: (logic.displayFields || []).map((displayField) => ({
      sectionId: sectionIdMap.get(displayField.sectionId) ?? 0,
      subSectionId: sectionIdMap.get(displayField.subSectionId) ?? 0,
      fields: (displayField.fields || [])
        .map((f) => ({ id: fieldIdMap.get(f.id) }))
        .filter((f) => f.id != null),
    })),
  }))
);

// Creates every question in `fields` inside `newSectionId` (0 = no section),
// preserving order via the same create -> reorder -> refetch pattern already
// used by duplicateField/duplicateSection, and records id/option mappings in
// `ctx` for the later Smart Rules/Actions pass (which needs the full map of
// old -> new ids across the whole tree, not just this one field's options).
const importFieldsInto = async (formId, fields, newSectionId, langId, ctx) => {
  let prevNewFieldId = 0;
  for (const field of fields || []) {
    const payload = buildImportFieldPayload(field, langId);
    const createRes = newSectionId > 0
      ? await FormEditService.createField(formId, newSectionId, payload)
      : await FormEditService.createUpdateFieldWithoutSection(formId, payload, false);

    if (createRes.status !== 200 && createRes.status !== 201) {
      throw new Error(`Failed to create question "${field.name}".`);
    }
    const newFieldId = createRes.data?.id;
    if (newFieldId == null) {
      throw new Error(`Failed to create question "${field.name}".`);
    }

    await FormEditService.reorderField(formId, newFieldId, { sectionId: newSectionId, prevFieldId: prevNewFieldId });

    const newFieldData = newSectionId > 0
      ? (await FormEditService.getSectionDetails(formId, newSectionId, langId)).data?.fields?.find((f) => f.id === newFieldId)
      : (await FormEditService.getFormDetails(formId, langId)).data?.fields?.find((f) => f.id === newFieldId);

    const optionIdMap = buildOptionIdMap(field, newFieldData || { id: newFieldId, options: [], gridConfig: null });

    ctx.fieldIdMap.set(field.id, newFieldId);
    ctx.fieldMeta.set(field.id, {
      newFieldId, newSectionId, optionIdMap, oldField: field,
    });

    prevNewFieldId = newFieldId;
  }
};

const buildSectionCreatePayload = (section, parentNewId, formType, langId) => {
  const params = {
    title: section.title,
    description: section.description,
    layout: section.layout || 'INLINE',
    displayTitle: section.displayTitle ?? true,
    sortOrder: section.sortOrder ?? 100,
    pageHelpText: section.pageHelpText,
  };
  if (parentNewId) params.parentSection = { id: parentNewId };
  if (formType === ASSESSMENTFORMTXT || formType === GENERAL_FORM) {
    params.scoringMethod = section.scoringMethod || 'NONE';
    params.weightage = section.weightage || '';
  }
  if (langId) params.selectedLanguage = { id: langId };
  if (section.uom) params.uom = section.uom;
  if (section.multiSupportEnabled) params.multiSupportEnabled = section.multiSupportEnabled;
  return params;
};

// Recreates a level of the Section/Subsection tree under `parentNewId` (0 for
// top-level), preserving both the original sibling order (via the same
// create -> reorder chain used elsewhere) and the exact nesting depth.
const importSectionTree = async (formId, sections, parentNewId, langId, formType, ctx) => {
  let prevNewSectionId = 0;
  for (const section of sections || []) {
    const params = buildSectionCreatePayload(section, parentNewId, formType, langId);
    const createRes = await FormEditService.createSection(formId, params, null);
    if (createRes.status !== 200 && createRes.status !== 201) {
      throw new Error(`Failed to create section "${section.title}".`);
    }
    const newSectionId = createRes.data?.id;
    if (newSectionId == null) {
      throw new Error(`Failed to create section "${section.title}".`);
    }

    await FormEditService.reorderSection(formId, newSectionId, {
      sectionId: parentNewId || 0,
      prevSectionId: prevNewSectionId,
    });

    ctx.sectionIdMap.set(section.id, newSectionId);

    await importFieldsInto(formId, section.fields, newSectionId, langId, ctx);
    await importSectionTree(formId, section.subSections, newSectionId, langId, formType, ctx);

    prevNewSectionId = newSectionId;
  }
};

// Second pass, run only once every section/field in the tree has been
// recreated: Smart Rules can reference ANY other section/field in the form
// (not just ones already created when their own field was), so this needs
// the complete old -> new id maps built during the first pass.
const applyActionsAndLogic = async (formId, ctx) => {
  const warnings = [];
  await Promise.all(Array.from(ctx.fieldMeta.entries()).map(async ([, meta]) => {
    const {
      newFieldId, newSectionId, optionIdMap, oldField,
    } = meta;
    if (oldField.actions?.length) {
      try {
        const clonedActions = cloneActionsForDuplicate(oldField.actions, optionIdMap);
        await FormEditService.createActions(formId, newFieldId, { actions: clonedActions });
      } catch (err) {
        warnings.push(`Actions could not be imported for "${oldField.name}".`);
      }
    }
    if (oldField.logicDefinitions?.length) {
      try {
        const remapped = remapLogicsForImport(oldField.logicDefinitions, optionIdMap, ctx.sectionIdMap, ctx.fieldIdMap);
        await FormEditService.saveFormFieldLogics(formId, newFieldId, newSectionId, { logicDefinitions: remapped });
      } catch (err) {
        warnings.push(`Smart Rules could not be imported for "${oldField.name}".`);
      }
    }
  }));
  return warnings;
};

// Recreates an entire exported form tree (Sections -> Subsections ->
// Questions -> Options/Smart Rules/Actions) inside the given (already open)
// form, via the same per-item create/reorder endpoints the rest of the
// Builder already uses for Create/Duplicate. Returns a summary the caller can
// surface to the user.
export const importFormIntoForm = async (formId, langId, formType, exportObject) => {
  const validation = validateImportFile(exportObject);
  if (!validation.valid) {
    const err = new Error(validation.errors.join(' '));
    err.validationErrors = validation.errors;
    throw err;
  }

  const ctx = { sectionIdMap: new Map(), fieldIdMap: new Map(), fieldMeta: new Map() };

  await importFieldsInto(formId, exportObject.fields, 0, langId, ctx);
  await importSectionTree(formId, exportObject.sections, 0, langId, formType, ctx);
  const warnings = await applyActionsAndLogic(formId, ctx);

  return {
    sectionsCreated: ctx.sectionIdMap.size,
    fieldsCreated: ctx.fieldIdMap.size,
    warnings,
  };
};
